import ILogger, { LogLevel, LoggerConfig } from "./ILogger"

class Logger implements ILogger {
    private logLevel: LogLevel
    private depthLimit: number
    private prettyPrintOutput: boolean
    private colors: any | null = null
    private console: Console

    constructor(config: LoggerConfig = {}) {
        const {
            logLevel = LogLevel.Info,
            depthLimit = 6,
            prettyPrintOutput = false,
            console = global.console
        } = config

        this.logLevel = logLevel
        this.depthLimit = depthLimit
        this.prettyPrintOutput = prettyPrintOutput
        this.console = console

        if (prettyPrintOutput) {
            import("./colors")
                .then(c => {
                    this.colors = c.default
                })
                .catch(e => {
                    this.console.info(
                        `Logger: LOG_PRETTY_PRINT is set to ${prettyPrintOutput}, but it seems the logger is run in an environment without full Node API compatibility. Therefore log colors have been disabled.`
                    )
                })
        }
    }

    public getLogLevel(): LogLevel {
        return this.logLevel
    }

    public getDepthLimit(): number {
        return this.depthLimit
    }

    public getPrettyPrintOutput(): boolean {
        return this.prettyPrintOutput
    }

    private shouldLog(level: LogLevel): boolean {
        const isProd = process.env.NEXT_PUBLIC_ENVIRONMENT === "production"

        if (isProd) {
            return level >= LogLevel.Warn
        }

        return level >= this.logLevel
    }

    private isErrorMessage(message: any): message is Error {
        return message instanceof Error
    }

    private isStringMessage(message: any): message is string {
        return typeof message === "string"
    }

    private hasOptionalParams(params?: any[]): boolean {
        return !!params?.length
    }

    private formatErrorMessage(error: Error): string {
        const baseMessage = `Error: ${error.message}`

        const stackTrace = error.stack ? `\nStack Trace: ${error.stack}` : ""

        return `${baseMessage}${stackTrace}`
    }

    private syntaxHighlight(json: string) {
        if (!this.prettyPrintOutput || !this.colors) return json

        json = json.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

        return json.replace(
            /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
            match => {
                let cls = "number"
                if (/^"/.test(match)) {
                    if (/:$/.test(match)) {
                        cls = "key"
                    } else {
                        cls = "string"
                    }
                } else if (/true|false/.test(match)) {
                    cls = "boolean"
                } else if (/null/.test(match)) {
                    cls = "null"
                }

                switch (cls) {
                    case "string":
                        return this.colors.green(match)
                    case "key":
                        return this.colors.yellow(match)
                    case "number":
                        return this.colors.red(match)
                    case "boolean":
                        return this.colors.blue(match)
                    case "null":
                        return this.colors.gray(match)
                    default:
                        return match
                }
            }
        )
    }

    private limitDepth = (obj: any, depth: number): any => {
        type JSONObject = { [key: string]: any }

        if (depth <= 0) {
            return `Log depth limit reached: ${this.depthLimit}`
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.limitDepth(item, depth - 1))
        } else if (typeof obj === "object" && obj !== null) {
            const result: JSONObject = {}
            for (const key in obj) {
                if (Object.hasOwnProperty.call(obj, key)) {
                    result[key] = this.limitDepth(obj[key], depth - 1)
                }
            }
            return result
        } else {
            return obj
        }
    }

    private isUnserializable(value: any): boolean {
        return typeof value === "function" || typeof value === "symbol"
    }

    private safelyStringify(obj: any): string {
        if (this.isUnserializable(obj)) {
            return `Unserializable value of type ${typeof obj}`
        }

        const replacer = (key: string, value: any) =>
            typeof value === "undefined" ? "undefined" : value

        try {
            const stringified = JSON.stringify(
                this.limitDepth(obj, this.depthLimit),
                replacer,
                this.prettyPrintOutput ? 2 : 0
            )
            return this.prettyPrintOutput
                ? this.syntaxHighlight(stringified)
                : stringified
        } catch {
            return "Unserializable message"
        }
    }

    private formatMessage(level: LogLevel, message: any, params?: any[]): string {
        const baseMessage = this.isErrorMessage(message)
            ? this.formatErrorMessage(message)
            : this.isStringMessage(message)
              ? message
              : this.safelyStringify(message)

        const additionalParams = this.hasOptionalParams(params)
            ? ` - ${this.safelyStringify(params)}`
            : ""

        return `[${new Date().toISOString()}][${LogLevel[
            level
        ].toUpperCase()}]: ${baseMessage}${additionalParams}`
    }

    private logToConsole(level: LogLevel, formattedMessage: string): void {
        const colorizedMessage =
            this.prettyPrintOutput && this.colors
                ? {
                      [LogLevel.Error]: this.colors.red(formattedMessage),
                      [LogLevel.Warn]: this.colors.yellow(formattedMessage),
                      [LogLevel.Info]: this.colors.green(formattedMessage),
                      [LogLevel.Debug]: this.colors.blue(formattedMessage),
                      [LogLevel.Trace]: this.colors.magenta(formattedMessage)
                  }[level]
                : formattedMessage

        const logMethod = {
            [LogLevel.Error]: this.console.error,
            [LogLevel.Warn]: this.console.warn,
            [LogLevel.Info]: this.console.info,
            [LogLevel.Debug]: this.console.debug,
            [LogLevel.Trace]: this.console.trace
        }[level]

        if (logMethod) {
            logMethod(colorizedMessage)
        }
    }

    log(message: any, ...optionalParams: any[]) {
        this.logMessage(LogLevel.Info, message, ...optionalParams)
    }

    debug(message: any, ...optionalParams: any[]) {
        this.logMessage(LogLevel.Debug, message, ...optionalParams)
    }

    info(message: any, ...optionalParams: any[]) {
        this.logMessage(LogLevel.Info, message, ...optionalParams)
    }

    warn(message: any, ...optionalParams: any[]) {
        this.logMessage(LogLevel.Warn, message, ...optionalParams)
    }

    error(message: any, ...optionalParams: any[]) {
        this.logMessage(LogLevel.Error, message, ...optionalParams)
    }

    trace(message: any, ...optionalParams: any[]) {
        this.logMessage(LogLevel.Trace, message, ...optionalParams)
    }

    private logMessage(level: LogLevel, message: any, ...optionalParams: any[]) {
        if (!this.shouldLog(level)) return

        const formattedMessage = this.formatMessage(level, message, optionalParams)

        this.logToConsole(level, formattedMessage)
    }
}

export default Logger
